import {getSortDirection, setSortDirection} from 'services/utils/tableUtils'
import {useUrlParams} from 'services/hooks'
import tables from 'constants/tables'
import {useCallback, useState, useEffect, useRef} from 'react'

const DELAY = 1000 // 1000ms delay

const useTableSort = (itemName, itemKey) => {
    const tableName = `${itemName}s`
    const {urlParams, setUrlParam} = useUrlParams()
    const {[`${itemName}_sort`]: sort} = urlParams
    const urlDirection = getSortDirection(itemKey, sort)

    // Local UI state for immediate feedback
    const [localDirection, setLocalDirection] = useState(urlDirection)

    // Ref for tracking the timeout
    const timeoutRef = useRef(null)
    const pendingUpdateRef = useRef(null)

    useEffect(() => {
        const defaultDesc = tables[tableName]?.sortTableKeysDefaultDesc || []
        if (!sort && defaultDesc.includes(itemKey)) {
            setLocalDirection('desc')
            setSortDirection(itemName, itemKey, 'desc', setUrlParam)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    // Keep local state in sync with URL
    useEffect(() => {
        setLocalDirection(urlDirection)
    }, [urlDirection])

    // Handle URL updates with delay
    useEffect(() => {
        if (pendingUpdateRef.current !== null) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }

            timeoutRef.current = setTimeout(() => {
                setSortDirection(itemName, itemKey, pendingUpdateRef.current, setUrlParam)
                pendingUpdateRef.current = null
                timeoutRef.current = null
            }, DELAY)
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [itemName, itemKey, setUrlParam])

    const onClickDirection = useCallback(() => {
        const newDirection = localDirection === 'asc' ? 'desc' : 'asc'
        setLocalDirection(newDirection) // Update UI immediately
        pendingUpdateRef.current = newDirection // Queue the URL update
    }, [localDirection])
    const showSort = tables[tableName]?.sortTableKeys?.includes(itemKey)
    return {showSort, onClickDirection, direction: localDirection}
}

export default useTableSort
